import { Button, DesktopModal, Divider, Empty, Flex, IconFont, Select, Tag } from '@byecode/ui'
import {
    EndNodeType,
    ListItem4ByecodeUi,
    NODE_CARD_WIDTH,
    TriggerNodeType,
    useAtomAction,
    WorkflowRunType,
    WorkflowType,
    X_OFFSET
} from '@lighthouse/shared'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { addWorkflowAtom } from '@/atoms/workflow/action'
import { workflowLabelMap } from '@/constants/flow'
import { FlowDetail, type FlowDetailRef } from '@/containers/FlowDetail'
import { generateNode } from '@/containers/FlowDetail/utils/addNode'
import { FlowDetailShape, useFlowContainerContext } from '@/contexts/FlowContext'
import * as srv from '@/services'
import type { WorkflowResult } from '@/services/types'

import * as SC from '../../styles'

const SCxSubProcessSelectorWrapper = styled.div``

const SCxApprovalFlowWrapper = styled.div`
    position: relative;
    width: 100%;
    margin-top: 6px;
    overflow: hidden;
    padding: 0 8px;
`

const SCxApprovalFlowContainer = styled.div`
    width: 100%;
    border-radius: 6px;
    background-color: var(--color-gray-100);
`

const SCxApprovalFlowContent = styled.div`
    position: relative;
    height: 245px;
    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }
`

const SCxApprovalFlowEdit = styled(Button)`
    margin-top: 12px;
`

const SCxApprovalCreatorButton = styled.div`
    display: flex;
    align-items: center;
    padding: 10px 16px;
    height: 36px;
    gap: 8px;
    font-size: var(--font-size-normal);
    color: var(--color-black);
    cursor: pointer;

    &:hover {
        background-color: var(--color-gray-100);
    }
`

interface SubProcessSelectorProps {
    value: string | undefined
    workflows: WorkflowResult
    style?: React.CSSProperties
    onChange?: (value: string) => void
}

export const SubProcessSelector: React.FC<SubProcessSelectorProps> = ({ value, workflows, style, onChange }) => {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    const flowDetailRef = useRef<FlowDetailRef>(null)
    const { run: addWorkflow } = useAtomAction(addWorkflowAtom)
    const { setDisable } = useFlowContainerContext()
    const flowNodesOptions = useMemo(
        () =>
            workflows
                .filter(workflow => workflow.type === 'subProcess')
                .map(item => ({
                    label: item.name,
                    value: item.id,
                    // icon: <IconFont type="Reorder" fill="var(--color-green-500)" size={16} />
                    icon: 'Reorder'
                })),
        [workflows]
    )

    const flow = useMemo(() => {
        return workflows.find(item => item.id === value)
    }, [value, workflows])

    const handleClose = useCallback(() => {
        setDisable(false)
        setOpen(false)
    }, [setDisable])

    const handleOpenFlowModal = useCallback(
        (id?: string, onClose?: () => void) => {
            const flowId = id ?? value
            if (!flowId) {
                return
            }
            setDisable(true)
            setOpen(true)
        },
        [setDisable, value]
    )

    const handleSubProcessCreate = useCallback(async () => {
        // window.open(workflowUrl, '_blank')
        // const flowInitInfo = addNewTriggerNodeWithEdges({
        //     nodeType: TriggerNodeType.ARG_TRIGGER,
        //     name: actionNodeInfoMap[TriggerNodeType.ARG_TRIGGER].label,
        //     config: {
        //         args: []
        //     }
        // })
        const newSubProcessNode = generateNode(TriggerNodeType.ARG_TRIGGER)
        const endNode = generateNode(EndNodeType.END, {
            position: {
                x: NODE_CARD_WIDTH * 2 + X_OFFSET * 2,
                y: 0
            }
        })
        const { label } = workflowLabelMap['subProcess']

        const newWorkflow = await srv.createFlow({
            name: `未命名${label}`,
            type: WorkflowType.SUB_PROCESS,
            runType: WorkflowRunType.SEQUENTIAL,
            description: `未命名${label}的描述`,
            edges: [],
            nodes: [newSubProcessNode, endNode]
        })

        if (newWorkflow) {
            addWorkflow(newWorkflow)
            onChange?.(newWorkflow.id)
            handleOpenFlowModal(newWorkflow.id)
        }
    }, [addWorkflow, handleOpenFlowModal, onChange])

    return (
        <SCxSubProcessSelectorWrapper style={style} ref={ref}>
            <ListItem4ByecodeUi>
                <SC.FormItemLabelWrapper>
                    <SC.FormItemLabel>选择子流程</SC.FormItemLabel>
                </SC.FormItemLabelWrapper>
                <SC.FormItemContent>
                    <Select
                        searchable
                        placeholder="请选择子流程"
                        value={value}
                        clearable
                        options={flowNodesOptions}
                        onChange={onChange}
                        emptyComponent={
                            <Empty
                                icon={<IconFont type="NoData" color="var(--color-gray-400)" size={20} />}
                                description="没有子流程"
                                styles={{ root: { height: 100, padding: '25px 0' } }}
                            />
                        }
                        dropdownProps={{
                            extra: (
                                <>
                                    <Divider color="var(--color-gray-200)" my={8} />
                                    <SCxApprovalCreatorButton onClick={handleSubProcessCreate}>
                                        <IconFont type="Add" size={16} style={{ color: 'var(--color-gray-400)' }} />
                                        创建子流程
                                    </SCxApprovalCreatorButton>
                                </>
                            )
                        }}
                    />
                </SC.FormItemContent>
            </ListItem4ByecodeUi>

            {flow && (
                <>
                    <ListItem4ByecodeUi>
                        <SC.FormItemLabelWrapper>
                            <SC.FormItemLabel>子流程状态</SC.FormItemLabel>
                        </SC.FormItemLabelWrapper>
                        <Flex style={{ width: 180, flexShrink: 0 }} justifyContent="flex-end">
                            <Tag
                                radius={6}
                                background={flow.enabled ? 'var(--color-green-100)' : 'var(--color-red-100)'}
                                color={flow.enabled ? 'var(--color-green-500)' : 'var(--color-red-500)'}
                            >
                                {flow.enabled ? '已启用' : '未启用'}
                            </Tag>
                        </Flex>
                    </ListItem4ByecodeUi>
                    <SCxApprovalFlowWrapper>
                        <SCxApprovalFlowContainer>
                            <SCxApprovalFlowContent>
                                {flow && flow?.content && (
                                    <FlowDetail key={String(open)} scale={0.3} id={flow.id} shape={FlowDetailShape.BOX} />
                                )}
                            </SCxApprovalFlowContent>
                        </SCxApprovalFlowContainer>
                        <SCxApprovalFlowEdit
                            size="lg"
                            block
                            icon={<IconFont type="PencilSimple" color="var(--color-gray-400)" />}
                            onClick={() => handleOpenFlowModal(value)}
                        >
                            编辑子流程
                        </SCxApprovalFlowEdit>
                    </SCxApprovalFlowWrapper>
                </>
            )}

            <DesktopModal
                position="fixed"
                open={open}
                target={ref.current}
                closeOnClickOverlay
                styles={{
                    modal: {
                        width: '80vw',
                        height: '75vh'
                    },
                    body: {
                        padding: 0
                    }
                }}
                onClose={() => {
                    if (flowDetailRef.current) {
                        flowDetailRef.current.requestClose()
                        return
                    }

                    handleClose()
                }}
                withCloseIcon={false}
            >
                <FlowDetail ref={flowDetailRef} id={value || ''} shape={FlowDetailShape.MODAL} onClose={handleClose} />
            </DesktopModal>
        </SCxSubProcessSelectorWrapper>
    )
}
